import React from 'react';
import { SiteLayout, FLAME, INK, BODY, MUTED, BORDER, MONO } from './SiteLayout';

const LAST_UPDATED = 'March 25, 2026';
const COMPANY = 'Ignitor Ads';
const EMAIL = 'IgnitorAds.Support@gmail.com';
const SITE = 'ignitorads.co';

function Section({ title, children }) {
  return (
    <section style={{ marginBottom: '38px' }}>
      <h2 style={{
        fontSize: '22px', fontWeight: 600, color: INK, letterSpacing: '-.4px',
        marginBottom: '14px', paddingBottom: '10px', borderBottom: `1px solid ${BORDER}`,
      }}>{title}</h2>
      <div style={{ fontSize: '16px', color: '#3a352d', lineHeight: 1.8 }}>{children}</div>
    </section>
  );
}
function P({ children }) { return <p style={{ marginBottom: '12px' }}>{children}</p>; }
function UL({ items }) {
  return (
    <ul style={{ paddingLeft: '22px', marginBottom: '12px' }}>
      {items.map((item, i) => <li key={i} style={{ marginBottom: '8px' }}>{item}</li>)}
    </ul>
  );
}
const link = { color: FLAME, fontWeight: 600 };

export default function PrivacyPolicy() {
  return (
    <SiteLayout>

      {/* Hero */}
      <section style={{ maxWidth: '1180px', margin: '0 auto', padding: '80px 32px 24px', textAlign: 'center' }}>
        <p style={{ fontFamily: MONO, fontSize: '16px', letterSpacing: '1.8px', color: FLAME, textTransform: 'uppercase', margin: 0 }}>
          Legal
        </p>
        <h1 className="ia-hero-h1" style={{ fontSize: '52px', fontWeight: 600, letterSpacing: '-1.6px', margin: '18px 0 0' }}>
          Privacy Policy
        </h1>
        <p style={{ fontFamily: MONO, fontSize: '14px', color: MUTED, marginTop: '14px' }}>
          Last updated: {LAST_UPDATED}
        </p>
      </section>

      {/* Content */}
      <div style={{ maxWidth: '820px', margin: '0 auto', padding: '30px 32px 80px' }}>

        <div style={{
          background: 'rgba(255,255,255,.85)', border: `1px solid ${BORDER}`,
          borderLeft: `4px solid ${FLAME}`, borderRadius: '14px',
          padding: '20px 24px', marginBottom: '44px',
          fontSize: '16px', color: '#3a352d', lineHeight: 1.7,
        }}>
          <strong>Summary:</strong> We collect only what we need to run the Service. We never sell your data.
          You can request deletion at any time. We use industry-standard security practices.
        </div>

        <Section title="1. Who We Are">
          <P>{COMPANY} ("we", "us", "our") operates the AdOps compliance platform at {SITE}. We are the data controller for personal data collected through the Service.</P>
          <P>For privacy-related questions, contact us at <a href={`mailto:${EMAIL}`} style={link}>{EMAIL}</a>.</P>
        </Section>

        <Section title="2. Data We Collect">
          <P><strong>Account data:</strong> When you register, we collect your name, email address, company name, and password (stored as a hashed value — we never store your plain-text password).</P>
          <P><strong>Usage data:</strong> We automatically collect information about how you use the Service, including pages visited, features used, tool runs performed, and timestamps. This helps us improve the platform.</P>
          <P><strong>Tool input data:</strong> Data you submit to our tools — such as domain names, bundle IDs, ads.txt records, and report files — is processed solely to return results to you. This data is not retained after your session unless explicitly saved to your account (e.g., Bundle Tracker lists, Discrepancy Monitor configurations).</P>
          <P><strong>Payment data:</strong> Payment details are handled by our third-party payment processor. We store only a transaction reference and the last 4 digits of your card — never your full card details.</P>
          <P><strong>Communication data:</strong> If you contact us via email or our contact form, we retain that correspondence to respond to your enquiry.</P>
        </Section>

        <Section title="3. How We Use Your Data">
          <P>We use the data we collect to:</P>
          <UL items={[
            'Provide, operate, and maintain the Service',
            'Process your subscription and billing',
            'Authenticate your identity and protect your account',
            'Send transactional emails (e.g., account confirmation, password reset)',
            'Send product updates and feature announcements (you can opt out at any time)',
            'Diagnose technical issues and improve platform performance',
            'Comply with legal obligations',
          ]} />
          <P>We do <strong>not</strong> use your data for advertising, profiling for third-party purposes, or sell it to any third party under any circumstances.</P>
        </Section>

        <Section title="4. Data Retention">
          <P>We retain your account data for as long as your account is active. If you cancel or your account is terminated:</P>
          <UL items={[
            'Your account data and associated tool data (bundle lists, monitor configs, etc.) are retained for 30 days, then permanently deleted',
            'Billing records are retained for 7 years as required by financial regulations',
            'Anonymised, aggregated usage statistics that cannot identify you may be retained indefinitely',
          ]} />
        </Section>

        <Section title="5. Data Sharing">
          <P>We do not sell, trade, or rent your personal data. We may share limited data with trusted third-party service providers who help us operate the platform:</P>
          <UL items={[
            'Cloud infrastructure providers (for hosting and database services)',
            'Payment processors (for handling subscription billing)',
            'Email delivery services (for sending transactional emails)',
          ]} />
          <P>All third-party providers are bound by data processing agreements and are only permitted to use your data to provide their specific service to us.</P>
          <P>We may disclose data if required to do so by law, court order, or government authority, or to protect the rights, property, or safety of {COMPANY}, our users, or the public.</P>
        </Section>

        <Section title="6. Cookies and Tracking">
          <P>We use essential cookies to:</P>
          <UL items={[
            'Maintain your login session (authentication token)',
            'Remember your preferences within the dashboard',
          ]} />
          <P>We do <strong>not</strong> use advertising cookies, third-party tracking pixels, or analytics tools that share your data with third parties. You can disable cookies in your browser settings, but this will prevent you from logging in to the Service.</P>
        </Section>

        <Section title="7. Security">
          <P>We take reasonable technical and organisational measures to protect your data, including:</P>
          <UL items={[
            'All data transmitted between your browser and our servers is encrypted using TLS/HTTPS',
            'Passwords are hashed using industry-standard algorithms (bcrypt)',
            'Access to production databases is restricted to authorised personnel only',
            'Authentication tokens are signed using JWT with secure secrets',
          ]} />
          <P>No method of transmission over the internet is 100% secure. While we strive to protect your data, we cannot guarantee absolute security.</P>
        </Section>

        <Section title="8. Your Rights">
          <P>You have the following rights with respect to your personal data:</P>
          <UL items={[
            'Access — request a copy of the personal data we hold about you',
            'Correction — request that we correct inaccurate or incomplete data',
            'Deletion — request that we delete your personal data ("right to be forgotten")',
            'Portability — request your data in a machine-readable format',
            'Objection — object to certain types of processing (e.g., marketing emails)',
            'Withdrawal of consent — where processing is based on consent, you may withdraw it at any time',
          ]} />
          <P>To exercise any of these rights, email us at <a href={`mailto:${EMAIL}`} style={link}>{EMAIL}</a>. We will respond within 30 days.</P>
        </Section>

        <Section title="9. Children's Privacy">
          <P>The Service is intended for business use and is not directed at children under the age of 16. We do not knowingly collect personal data from anyone under 16. If you believe we have inadvertently collected data from a child, please contact us immediately and we will delete it.</P>
        </Section>

        <Section title="10. Changes to This Policy">
          <P>We may update this Privacy Policy from time to time. We will notify you of significant changes via email or a notice on the platform. The "Last updated" date at the top of this page reflects the most recent revision. Your continued use of the Service after changes take effect constitutes your acceptance of the updated policy.</P>
        </Section>

        <Section title="11. Contact Us">
          <P>For any privacy-related questions, data requests, or concerns, please contact:</P>
          <div style={{
            background: '#efece5', borderRadius: '12px', padding: '20px 24px',
            border: `1px solid ${BORDER}`, fontSize: '15px',
          }}>
            <div style={{ fontWeight: 700, color: INK, marginBottom: '6px' }}>{COMPANY} — Privacy</div>
            <div>Email: <a href={`mailto:${EMAIL}`} style={link}>{EMAIL}</a></div>
            <div>Website: <a href={`https://${SITE}`} style={link}>{SITE}</a></div>
          </div>
        </Section>

      </div>
    </SiteLayout>
  );
}
